import { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface SplashScreenProps {
    onFinish: () => void;
    minDisplayTime?: number;
}

const SMOOTH_EASE = [0.16, 1, 0.3, 1] as const;

export default function SplashScreen({ onFinish, minDisplayTime = 2800 }: SplashScreenProps) {
    const [isVisible, setIsVisible] = useState(true);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(false);
            setTimeout(onFinish, 700);
        }, minDisplayTime);
        return () => clearTimeout(timer);
    }, [minDisplayTime, onFinish]);

    return (
        <AnimatePresence>
            {isVisible && (
                <motion.div
                    initial={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.8, ease: SMOOTH_EASE }}
                    className="fixed inset-0 z-[9999] overflow-hidden flex flex-col items-center justify-center"
                    style={{
                        background:
                            'radial-gradient(circle at 50% 50%, #0a1a3e 0%, #050a1f 40%, #000000 100%)',
                    }}
                >
                    {/* ===== لایه ۱: نورهای محیطی (parallax blur) ===== */}
                    <AmbientLightBlobs />

                    {/* ===== لایه ۲: ذرات معلق در مدار ===== */}
                    <OrbitingParticles />

                    {/* ===== لایه ۳: قلب مرکزی (لوگو + نبض) ===== */}
                    <div className="relative z-10 flex flex-col items-center">
                        <PulsingLogo />
                        <BrandText />
                    </div>

                    {/* ===== لایه ۴: خط پیشرفت پایین ===== */}
                    <BottomProgress />
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/* ============================================================
   لایه ۱: نورهای محیطی — Blobهای تار که در فضای Splash شنا می‌کنند
   ============================================================ */
function AmbientLightBlobs() {
    const blobs = useMemo(
        () => [
            { id: 0, size: 400, top: '15%', left: '10%', delay: 0, color: 'rgba(59,130,246,0.18)' },
            { id: 1, size: 320, top: '60%', left: '70%', delay: 1.5, color: 'rgba(96,165,250,0.15)' },
            { id: 2, size: 280, top: '70%', left: '15%', delay: 3, color: 'rgba(147,197,253,0.12)' },
            { id: 3, size: 360, top: '20%', left: '75%', delay: 4.5, color: 'rgba(37,99,235,0.14)' },
        ],
        []
    );

    return (
        <div className="absolute inset-0 pointer-events-none">
            {blobs.map((b) => (
                <motion.div
                    key={b.id}
                    className="absolute rounded-full"
                    style={{
                        width: b.size,
                        height: b.size,
                        top: b.top,
                        left: b.left,
                        background: `radial-gradient(circle, ${b.color} 0%, transparent 70%)`,
                        filter: 'blur(40px)',
                    }}
                    animate={{
                        x: [0, 40, -30, 0],
                        y: [0, -30, 20, 0],
                        scale: [1, 1.15, 0.95, 1],
                    }}
                    transition={{
                        duration: 12 + b.id * 2,
                        repeat: Infinity,
                        delay: b.delay,
                        ease: 'easeInOut',
                    }}
                />
            ))}
        </div>
    );
}

/* ============================================================
   لایه ۲: ذرات معلق در مدار — نقطه‌هایی که دور لوگو می‌چرخند
   ============================================================ */
function OrbitingParticles() {
    const particles = useMemo(
        () =>
            Array.from({ length: 12 }, (_, i) => ({
                id: i,
                angle: (i * 360) / 12,
                radius: 100 + (i % 3) * 35,
                size: 2 + (i % 2) * 1.5,
                delay: i * 0.15,
                duration: 3 + (i % 4),
            })),
        []
    );

    return (
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            {particles.map((p) => {
                const rad = (p.angle * Math.PI) / 180;
                const x = Math.cos(rad) * p.radius;
                const y = Math.sin(rad) * p.radius;

                return (
                    <motion.div
                        key={p.id}
                        className="absolute rounded-full bg-blue-300"
                        style={{
                            width: p.size,
                            height: p.size,
                            boxShadow: `0 0 ${p.size * 3}px ${p.size}px rgba(96,165,250,0.7)`,
                            marginLeft: x,
                            marginTop: y,
                        }}
                        animate={{
                            scale: [0.6, 1.4, 0.6],
                            opacity: [0.3, 1, 0.3],
                        }}
                        transition={{
                            duration: p.duration,
                            repeat: Infinity,
                            delay: p.delay,
                            ease: 'easeInOut',
                        }}
                    />
                );
            })}
        </div>
    );
}

/* ============================================================
   لایه ۳: قلب مرکزی — لوگو با نبض نور و مدار چرخان
   ============================================================ */
function PulsingLogo() {
    return (
        <div className="relative flex items-center justify-center">
            {/* نبض نور — سه موج پشت سر هم */}
            {[0, 1, 2].map((i) => (
                <motion.div
                    key={i}
                    className="absolute rounded-full border-2 border-blue-400/30"
                    style={{ width: 140, height: 140 }}
                    animate={{
                        scale: [1, 2.5, 2.5],
                        opacity: [0.6, 0, 0],
                    }}
                    transition={{
                        duration: 2.5,
                        repeat: Infinity,
                        delay: i * 0.8,
                        ease: 'easeOut',
                    }}
                />
            ))}

            {/* مدار چرخان با نقطه نورانی */}
            <motion.div
                className="absolute rounded-full"
                style={{ width: 180, height: 180 }}
                animate={{ rotate: 360 }}
                transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
            >
                <div
                    className="absolute w-2 h-2 rounded-full bg-blue-400"
                    style={{
                        top: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        boxShadow: '0 0 12px 3px rgba(96,165,250,0.9)',
                    }}
                />
            </motion.div>

            {/* مدار دوم (معکوس) */}
            <motion.div
                className="absolute rounded-full"
                style={{ width: 220, height: 220 }}
                animate={{ rotate: -360 }}
                transition={{ duration: 14, repeat: Infinity, ease: 'linear' }}
            >
                <div
                    className="absolute w-1.5 h-1.5 rounded-full bg-blue-300"
                    style={{
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        boxShadow: '0 0 10px 2px rgba(147,197,253,0.8)',
                    }}
                />
            </motion.div>

            {/* لوگوی اصلی */}
            <motion.div
                className="relative w-32 h-32 rounded-full flex items-center justify-center"
                style={{
                    background:
                        'radial-gradient(circle, rgba(30,58,138,0.4) 0%, rgba(10,26,62,0.8) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(96,165,250,0.3)',
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                    type: 'spring',
                    stiffness: 160,
                    damping: 15,
                    delay: 0.2,
                }}
            >
                <motion.img
                    src="/app/assets/images/logoStreetWhite.webp"
                    alt="StreetCard"
                    className="w-20 h-20 object-contain"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{
                        opacity: 1,
                        scale: 1,
                        filter: [
                            'drop-shadow(0 0 8px rgba(96,165,250,0.6))',
                            'drop-shadow(0 0 24px rgba(96,165,250,1))',
                            'drop-shadow(0 0 8px rgba(96,165,250,0.6))',
                        ],
                    }}
                    transition={{
                        opacity: { duration: 0.8, delay: 0.5 },
                        scale: { duration: 0.8, delay: 0.5, ease: SMOOTH_EASE },
                        filter: {
                            duration: 2.4,
                            repeat: Infinity,
                            ease: 'easeInOut',
                        },
                    }}
                />
            </motion.div>
        </div>
    );
}

/* ============================================================
   لایه ۳ (ادامه): متن برند با خط نورانی
   ============================================================ */
function BrandText() {
    return (
        <div className="mt-12 text-center">
            <motion.h1
                className="text-3xl font-bold text-white tracking-[0.15em]"
                initial={{ opacity: 0, letterSpacing: '0.5em', y: 15 }}
                animate={{ opacity: 1, letterSpacing: '0.15em', y: 0 }}
                transition={{ delay: 0.8, duration: 1, ease: SMOOTH_EASE }}
                style={{
                    textShadow:
                        '0 0 30px rgba(96,165,250,0.5), 0 0 60px rgba(96,165,250,0.3)',
                }}
            >
                STREETCARD
            </motion.h1>

            {/* خط نورانی با گرادیانت */}
            <motion.div
                className="mx-auto mt-3 h-[1px] relative overflow-hidden"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 140, opacity: 1 }}
                transition={{ delay: 1.2, duration: 0.9, ease: SMOOTH_EASE }}
                style={{
                    background:
                        'linear-gradient(90deg, transparent 0%, rgba(96,165,250,0.8) 50%, transparent 100%)',
                }}
            >
                {/* پالس روی خط */}
                <motion.div
                    className="absolute top-0 h-full w-12"
                    style={{
                        background:
                            'linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)',
                    }}
                    animate={{ x: ['-100%', '300%'] }}
                    transition={{
                        duration: 1.8,
                        repeat: Infinity,
                        repeatDelay: 1.5,
                        ease: 'easeInOut',
                    }}
                />
            </motion.div>

            <motion.p
                className="mt-4 text-white/50 text-xs tracking-[0.3em]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 0.8 }}
            >
                مدیریت هوشمند پرداخت‌ها
            </motion.p>
        </div>
    );
}

/* ============================================================
   لایه ۴: خط پیشرفت پایین
   ============================================================ */
function BottomProgress() {
    return (
        <motion.div
            className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8, duration: 0.6 }}
        >
            <div className="w-32 h-[2px] bg-white/10 rounded-full overflow-hidden">
                <motion.div
                    className="h-full rounded-full"
                    style={{
                        background:
                            'linear-gradient(90deg, rgba(59,130,246,0.6), rgba(147,197,253,1))',
                        boxShadow: '0 0 10px rgba(96,165,250,0.8)',
                    }}
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 2.4, delay: 1.9, ease: 'easeInOut' }}
                />
            </div>

            <motion.p
                className="text-white/30 text-[10px] tracking-widest"
                animate={{ opacity: [0.3, 0.7, 0.3] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
                v ۱.۰.۰
            </motion.p>
        </motion.div>
    );
}